import {
  AdditionalInsuredFormComponent,
  CoverageAdjustmentFormComponent,
  getModalTypeFromChangeType,
  PolicyEndorsementFormComponent,
  PolicyModalTypeEnum,
} from '@program-suite/feature-policy-endorsements';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GridOptions, GridEmitter, UiGridComponent } from '@program-suite/ui-grid';
import { Sort } from '@angular/material/sort';
import { LookupStore, PolicyChangeTypeEnum, PolicyEndorsementsService } from '@program-suite/data-common';
import { PendingRequestStore } from '../data/pending-request.store';
import { Router } from '@angular/router';
import { PendingRequestGridRowViewModel } from '@program-suite/data-common/program';
import { ModalService, ModalType } from '@program-suite/ui';
import { PendingRequestModalComponent } from '../pending-request-modal/pending-request-modal.component';

@Component({
  selector: 'lib-pending-requests',
  imports: [CommonModule, UiGridComponent],
  templateUrl: './pending-requests.component.html',
  styleUrl: './pending-requests.component.scss',
})
export class PendingRequestsComponent implements OnInit {
  // TODO - add export actions
  pendingRequestStore = inject(PendingRequestStore);
  policyChangeService = inject(PolicyEndorsementsService);
  lookupStore = inject(LookupStore);
  defaultSortColumn = 'lastUpdated';
  defaultSortDirection = 'DESCENDING';
  agencyId = signal<number | null | undefined>(null);
  tenantId = signal<number | null | undefined>(null);
  totalNumberOfItems = computed<number>(() =>
    this.pendingRequestStore.resource.value() ? this.pendingRequestStore.resource.value()?.totalCount ?? 0 : 0,
  );

  data = computed<PendingRequestGridRowViewModel[]>(() =>
    this.pendingRequestStore.resource.value() ? this.pendingRequestStore.resource.value()?.rows ?? [] : [],
  );
  gridOptions = signal<GridOptions>({
    filterData: [
      { name: 'Search', formControlName: 'searchTerm', type: 'search' },
      { name: 'Type', formControlName: 'RequestType', type: 'dropdown', lookup: this.lookupStore.requestTypes },
      { name: 'Program', formControlName: 'programId', type: 'dropdown', lookup: this.lookupStore.programs },
      { name: 'Tenant', formControlName: 'tenantId', type: 'dropdown', lookup: this.lookupStore.tenants },
      {
        name: 'Agency',
        formControlName: 'agencyId',
        type: 'dropdown',
        lookup: this.lookupStore.agencies,
        dependencies: ['tenantId'],
      },
      {
        name: 'Producer',
        formControlName: 'producerId',
        type: 'dropdown',
        lookup: this.lookupStore.agents,
        dependencies: ['agencyId', 'tenantId'],
      },
    ],
    filterValues: this.pendingRequestStore.filterValues,
    openFilters: true,
    columns: [
      { header: 'Last Updated', controlName: 'lastUpdated', displayPipe: 'date' },
      { header: 'Request Type', controlName: 'requestType' },
      { header: 'Submission #', controlName: 'submissionNumber', minWidth: '136px' },
      { header: 'Policy #', controlName: 'policyNumber', minWidth: '100px' },
      { header: 'Program', controlName: 'program' },
      { header: 'Business Name', controlName: 'businessName' },
      { header: 'Effective Date', controlName: 'effectiveDate', displayPipe: 'date' },
      { header: 'Tenant', controlName: 'tenantName' },
      { header: 'Agency', controlName: 'agencyName', displayPipe: 'truncate' },
      { header: 'Producer Name', controlName: 'producerName' },
      {
        header: 'Manage',
        controlName: 'action',
        actionColumnitems: [
          {
            type: 'dropdown',
            buttonAction: 'action',
            conditionalDisplay: { calculateDisplay: () => 'ellipsis', column: 'status' },
            dropdownItems: computed(() => {
              const items = [
                { name: 'Manage Request', buttonAction: 'manageRequest' },
                { buttonAction: 'viewDetails', 
                  conditionalName: { 
                    calculateDisplay: (rowColumnValue: string) => {
                      switch (rowColumnValue) {
                        case null:
                          return 'View Submission';
                        default:
                          return 'View Policy';
                      }
                    }, column: 'policyGuid'
                  },
              },
              ];
              return items;
            })
          },
        ],
      },
    ],
    data: this.data,
    totalNumberOfItems: this.totalNumberOfItems,
    frontEndPagination: false,
    fetchData: false,
    displaySearch: false,
  });

  constructor(private router: Router, private modalService: ModalService) {}

  ngOnInit(): void {
    this.lookupStore.loadLookup('tenants');
    this.lookupStore.loadLookup('programs');
    this.lookupStore.loadLookup('requestTypes');
    // reload grid everytime this page is hit
    if (this.pendingRequestStore.filterValues() !== null) {
      this.pendingRequestStore.resource.reload();
    }
  }

  fetchAll(rawParams?: any) {
    const { sortOrder, agencyId, tenantId } = rawParams;
    //#region Update and load lookups
    if (!tenantId) {
      this.lookupStore.clearLookup('agencies');
      this.tenantId.set(null);
    } else if (tenantId && this.tenantId() !== tenantId) {
      this.tenantId.set(tenantId);
      this.lookupStore.loadLookup('agencies', tenantId);
    }
    if (!agencyId) {
      this.lookupStore.clearLookup('agents');
      this.agencyId.set(null);
    } else if (agencyId && this.agencyId() !== agencyId) {
      this.agencyId.set(agencyId);
      this.lookupStore.loadLookup('agents', agencyId);
    }
    //#endregion
    const newSortOrder = this.sortField(sortOrder);
    const params = {
      ...rawParams,
      dateFrom: '',
      dateTo: '',
      SortField: newSortOrder
        ? newSortOrder.active && newSortOrder.direction
          ? newSortOrder.active
          : this.defaultSortColumn
        : this.defaultSortColumn,
      SortDirection: newSortOrder ? newSortOrder.direction ?? this.defaultSortDirection : this.defaultSortDirection,
    };
    this.pendingRequestStore.setFilterValues(params);
  }

  sortField(sort: Sort) {
    let activeSort: string = this.defaultSortColumn;
    let direction: string | null = this.defaultSortDirection;
    if (sort !== null && sort !== undefined) {
      activeSort = sort.active;
      switch (true) {
        case sort.active === 'effectiveDate':
          activeSort = 'policyEffectiveDate';
          break;
      }
      switch (true) {
        case sort.direction === 'desc':
          direction = 'DESCENDING';
          break;
        case sort.direction === 'asc':
          direction = 'ASCENDING';
          break;
        case sort.direction === '':
          direction = null;
          break;
      }
    }
    return { active: activeSort, direction: direction };
  }

  openPendingRequestModal(gridRow: PendingRequestGridRowViewModel) {
    this.modalService.open(PendingRequestModalComponent, {
      modalType: ModalType.FullScreen,
      data: gridRow,
    }, (result) => {
      if (result) {
        this.pendingRequestStore.resource.reload();
      }
    });
  }

  async actionEvent(event: GridEmitter) {
    const data = event.data as PendingRequestGridRowViewModel;
    if (event.action === 'fetchAll') {
      this.fetchAll(data);
    } else if (event.action === 'formChanges') {
      this.fetchAll(data);
    } else if (event.action === 'clearFilters') {
      this.fetchAll(data);
    } else if (event.action === 'onDestroy') {
      // on Destroy
    } else if (event.action === 'manageRequest') {
      switch (data.requestType) {
        case 'Endorsement':
          const policyChange = await this.policyChangeService.getPolicyChange(data.id!);
          const modalType = getModalTypeFromChangeType(policyChange.policyChangeType);

          switch (modalType) {
            case PolicyModalTypeEnum.Choice:
              this.modalService.open(PolicyEndorsementFormComponent, {
                modalType: ModalType.Medium,
                data: {
                  mode: 'edit',
                  ...policyChange,
                },
              }, (result) => {
                if (result) {
                  this.pendingRequestStore.resource.reload();
                }
              });
              break;
            case PolicyModalTypeEnum.AdditionalInsured:
              var AIData = {
                mode: 'edit',
                ...data,
                ...policyChange,
              };
              this.router.navigate(['/pending-request/additional-insured', policyChange.policyChangeGuid], {
                state: AIData,
              });
              break;
            case PolicyModalTypeEnum.CoverageAdjustment:
              var coverageData = {
                mode: 'edit',
                subTitle: `${data.businessName} | ${data.policyNumber}`,
                businessName: data.businessName,
                policyNumber: data.policyNumber,
                ...policyChange,
              };
              this.router.navigate(['/pending-request/coverage-adjustment', policyChange.policyChangeGuid], {
                state: coverageData,
              });
          }
          break;
        default:
          this.openPendingRequestModal(data);
      }
    } else if (event.action === 'viewDetails') {
      switch (data.policyGuid) {
        case null:
          this.router.navigate(['submissions', 'summary', data.id]);
          
          break;
        default:
          this.router.navigate(['policies', 'summary', data.policyGuid]);
      }
    }
  }
}
